import { Component, DestroyRef, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DeviceLockService } from '../../services/device/device-lock.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, finalize, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { faBan, faCheck, faChevronDown, faChevronRight, faLock } from '@fortawesome/free-solid-svg-icons';
import { ConcurrentUsageService } from '../../services/concurrent-usage/concurrent-usage.service';
import { DeviceLockFingerprint, LockState, prettifyDeviceLockFingerprint } from '../../services/device/device.utils';
import { exhaustMap, Observable, of, Subject } from 'rxjs';
import { safeStringify } from '@evorait/pace-components';
import { ProgressbarComponent } from 'ngx-bootstrap/progressbar';
import { DeviceLockInfoComponent } from './device-lock-info-component/device-lock-info.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { YesNoModalDialogComponent } from '../yes-no-modal-dialog/yes-no-modal-dialog.component';
import { YesNoModalDialogModel } from '../yes-no-modal-dialog/yes-no-modal-dialog.model';
import { ConnectivityService } from '../../services/connectivity/connectivity.service';
import { LetDirective } from '@ngrx/component';
import { LogInstanceToken, PaceInsightsService } from '@evorait/pace-logging';
import { isDefined } from '../../utilities/is-defined';

@Component({
    selector: 'pace-core-device-lock-management-modal',
    imports: [
        CommonModule,
        FontAwesomeModule,
        TranslateModule,
        ProgressbarComponent,
        DeviceLockInfoComponent,
        LetDirective,
    ],
    templateUrl: './device-lock-management-modal.component.html',
    styleUrls: ['./device-lock-management-modal.component.scss'],
})
export class DeviceLockManagementModalComponent implements OnInit, OnDestroy {
    @Input()
    public releaseLockCleanupHook$: Observable<boolean> | undefined;

    private readonly deviceLockService = inject(DeviceLockService);
    private readonly concurrentUsageService = inject(ConcurrentUsageService);
    public readonly modalInstance = inject(BsModalRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly paceInsights = inject(PaceInsightsService);
    private readonly log = inject(LogInstanceToken);
    private readonly translate = inject(TranslateService);
    private readonly modalService = inject(BsModalService);
    private readonly connectivityService = inject(ConnectivityService);

    public readonly lockState$ = this.deviceLockService.lockState$;
    public isOnline$ = this.connectivityService.isOnline$;

    public closable: boolean | undefined = undefined;

    // TODO: add observable to track, if the user is allowed to reset the device lock:
    public readonly aquireLockFailed = new Subject<void>();
    public readonly allowUserToResetLock$: Observable<boolean> = this.aquireLockFailed.asObservable().pipe(
        switchMap(() => this.lockState$.pipe(take(1))),
        map((state) => this.deviceLockService.isDeviceLockAllowedToBeRemoved(state)),
    );
    public readonly isResetWarningDisplayed = signal(false);

    public readonly faCheck = faCheck;
    public readonly faChevronRight = faChevronRight;
    public readonly faChevronDown = faChevronDown;
    public readonly myFingerprint = this.deviceLockService.deviceFingerprint;

    public isDetailExpanded = false;
    public loading = false;
    public actionLog: Array<{
        ts: string;
        message: string;
        props?: { [key: string]: string | boolean | number | null };
        emphasize?: boolean;
    }> = [];

    public isDev = window.location.hostname === 'localhost';

    private forceRemoveLock$ = new Subject<void>();
    private tryAcquireLock$ = new Subject<void>();
    private releaseLock$ = new Subject<void>();

    public constructor() {
        this.forceRemoveLock$
            .asObservable()
            .pipe(
                withLatestFrom(this.lockState$),
                exhaustMap(([, lockState]) => this.forceRemoveLockAndAcquireLock(lockState)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((gotLock) => this.actionAfterLockAcquireAttempt(gotLock));
        this.tryAcquireLock$
            .asObservable()
            .pipe(
                switchMap(() => {
                    if (this.deviceLockService.deviceFingerprint.isPrivateBrowser) {
                        return this.showIncognitoWarningModal();
                    } else {
                        return of(undefined);
                    }
                }),
                exhaustMap(() => this.tryAcquireLock()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((gotLock) => this.actionAfterLockAcquireAttempt(gotLock));
        this.releaseLock$
            .asObservable()
            .pipe(
                exhaustMap(() => this.releaseLock()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((releasedLock) => {
                if (releasedLock) {
                    this.pushAction('DEVICE_LOCK.actionLog.releasedLock', undefined, true);
                    this.modalInstance.setClass(ModalClass.MODAL_FULLSCREEN);
                } else {
                    this.pushAction('DEVICE_LOCK.actionLog.releaseFailed', undefined, true);
                }
            });
    }

    public ngOnInit(): void {
        this.deviceLockService.setLockModalOpen(true);
        this.lockState$.pipe(take(1)).subscribe((lockState) => {
            this.closable = lockState.ownedByMe || !this.deviceLockService.isInitialized;
        });
    }

    public ngOnDestroy(): void {
        this.deviceLockService.setLockModalOpen(false);
    }

    public triggerForceRemoveLock(): void {
        this.forceRemoveLock$.next();
    }

    private forceRemoveLockAndAcquireLock(lockState: LockState): Observable<boolean> {
        this.isResetWarningDisplayed.set(false);
        this.loading = true;
        this.paceInsights.trackEvent('DeviceLock::ForceUnlocking', {
            myFingerPrint: this.myFingerprint,
            lockState,
            isDev: this.isDev,
        });
        return this.deviceLockService.forceRemoveLock().pipe(
            switchMap(() => this.acquireLock()),
            take(1),
            catchError((err) => {
                this.pushAction('DEVICE_LOCK.actionLog.error', { error: safeStringify(err) }, true);
                return of(false);
            }),
            finalize(() => {
                this.loading = false;
            }),
        );
    }

    public reload(): void {
        window.location.reload();
    }

    public triggerAcquireLock(): void {
        this.tryAcquireLock$.next();
    }

    private tryAcquireLock(): Observable<boolean> {
        if (this.loading) {
            return of(false);
        }
        this.loading = true;
        this.paceInsights.trackEvent('DeviceLock::TryAcquireLock', {
            myFingerPrint: this.myFingerprint,
        });
        return this.acquireLock().pipe(
            take(1),
            catchError((err) => {
                this.pushAction('DEVICE_LOCK.actionLog.error', { error: safeStringify(err) }, true);
                return of(false);
            }),
            finalize(() => {
                this.loading = false;
            }),
        );
    }

    public triggerReleaseLock(): void {
        this.releaseLock$.next();
    }

    private releaseLock(): Observable<boolean> {
        this.closable = false;
        if (this.loading) {
            return of(false);
        }
        this.loading = true;
        this.deviceLockService.updateReleaseLockStatus(true);
        this.paceInsights.trackEvent('DeviceLock::releaseLock', {
            myFingerPrint: this.myFingerprint,
        });
        return this.deviceLockService.refreshState().pipe(
            switchMap(() => this.deviceLockService.lockState$),
            take(1),
            tap((state: LockState) => {
                if (state?.ownerFingerprint?.clientId) {
                    this.pushAction(`DEVICE_LOCK.actionLog.refreshStateResult`, {
                        acquiredAt: state.acquiredAt,
                        owner: this.prettifyFingerprint(state.ownerFingerprint),
                    });
                } else {
                    this.pushAction(`DEVICE_LOCK.actionLog.refreshEmptyStateResult`);
                }
            }),
            switchMap(() => {
                this.pushAction('DEVICE_LOCK.actionLog.uploading');
                return this.concurrentUsageService.uploadAll().pipe(
                    tap(() => {
                        this.pushAction('DEVICE_LOCK.actionLog.releasing');
                    }),
                    switchMap(() =>
                        isDefined(this.releaseLockCleanupHook$) ? this.releaseLockCleanupHook$ : of(undefined),
                    ),
                    switchMap(() => this.deviceLockService.release()),
                );
            }),
            take(1),
            catchError((err) => {
                this.pushAction('DEVICE_LOCK.actionLog.error', { error: safeStringify(err) }, true);
                return of(false);
            }),
            finalize(() => {
                this.loading = false;
                this.deviceLockService.updateReleaseLockStatus(false);
            }),
        );
    }

    private acquireLock(): Observable<boolean> {
        this.pushAction('DEVICE_LOCK.actionLog.refreshState');
        return this.deviceLockService.refreshState().pipe(
            switchMap(() => this.deviceLockService.lockState$),
            take(1),
            tap((state: LockState) => {
                if (state?.ownerFingerprint?.clientId) {
                    this.pushAction(`DEVICE_LOCK.actionLog.refreshStateResult`, {
                        acquiredAt: state.acquiredAt,
                        owner: this.prettifyFingerprint(state.ownerFingerprint),
                    });
                } else {
                    this.pushAction(`DEVICE_LOCK.actionLog.refreshEmptyStateResult`);
                }
            }),
            switchMap((state) => {
                if (state.ownedByMe) {
                    this.pushAction('DEVICE_LOCK.actionLog.alreadyOwnedByMe');
                    return of(true);
                }
                if (state.ownerFingerprint?.clientId) {
                    this.pushAction('DEVICE_LOCK.actionLog.stillOwnedByOther', {
                        acquiredAt: state.acquiredAt,
                        owner: this.prettifyFingerprint(state.ownerFingerprint),
                    });
                    return of(false);
                }
                return this.concurrentUsageService.downloadAll().pipe(
                    tap(() => {
                        this.pushAction('DEVICE_LOCK.actionLog.acquiring');
                    }),
                    mergeMap(() => this.deviceLockService.acquire().pipe(take(1))),
                );
            }),
        );
    }

    private pushAction(
        message: string,
        props?: { [key: string]: string | boolean | number | null },
        emphasize = false,
    ): void {
        this.log.info('DeviceLockManagementModel::Action', { message, props: safeStringify(props), emphasize });
        this.actionLog.unshift({ ts: new Date().toISOString(), message, props, emphasize });
    }

    public prettifyFingerprint(fingerprint: DeviceLockFingerprint): string {
        if (fingerprint) {
            return prettifyDeviceLockFingerprint(fingerprint);
        }
        return 'unknown device';
    }

    public toggleDetailsExpansion(): void {
        this.isDetailExpanded = !this.isDetailExpanded;
    }

    private actionAfterLockAcquireAttempt(gotLock: boolean): void {
        if (gotLock) {
            this.pushAction('DEVICE_LOCK.actionLog.acquiredSuccess', undefined, true);
            this.reload();
        } else {
            this.pushAction('DEVICE_LOCK.actionLog.didNotGetLock', undefined, true);
            this.aquireLockFailed.next();
        }
    }

    private showIncognitoWarningModal(): Observable<void> {
        const modalInstance = this.modalService.show(YesNoModalDialogComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM_NO_FULLSCREEN,
            ignoreBackdropClick: true,
            initialState: {
                model: new YesNoModalDialogModel(
                    this.translate.instant('INCOGNITO_WARNING_MODAL.Title'),
                    this.translate.instant('INCOGNITO_WARNING_MODAL.Message'),
                    this.translate.instant('INCOGNITO_WARNING_MODAL.Ok'),
                ),
            },
            ariaLabelledBy: 'yes-no-modal-title',
        });

        return (modalInstance.content?.modalResult ?? of(true)).pipe(
            take(1),
            map(() => undefined),
        );
    }

    protected readonly faBan = faBan;
    protected readonly faLock = faLock;
}
